import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase, TABLES } from '../api/db';
import { AIAnalysis, Task } from '../types';
import { Search, FileText, Code, ExternalLink, Plus, Tag } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { normalizeAIAnalysisRow, normalizeTaskRow } from '../utils/taskRecords';

interface GuideResult {
  analysis: AIAnalysis;
  task: Task | null;
}

const SearchGuides = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState<GuideResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const searchGuides = async () => {
    if (!searchTerm.trim()) {
      setResults([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { data: analysisRows } = await supabase
        .from(TABLES.AI_ANALYSES)
        .select('*')
        .order('created_at', { ascending: false })
        .limit(100);

      const guideResults: GuideResult[] = [];
      
      for (const row of (analysisRows ?? [])) {
        const analysis = normalizeAIAnalysisRow(row as Record<string, unknown>);
        
        let task: Task | null = null;
        if (analysis.taskId) {
          try {
            const { data: taskRow } = await supabase.from(TABLES.TASK_HATCH_SUBMISSIONS).select('*').eq('id', analysis.taskId).maybeSingle();
            if (taskRow) task = normalizeTaskRow(taskRow as Record<string, unknown>);
          } catch (err) {
            console.warn('Could not load task for analysis:', err);
          }
        }

        const searchLower = searchTerm.toLowerCase();
        const taskTitle = task?.title?.toLowerCase() || '';
        const taskSummary = task?.summary?.toLowerCase() || '';
        const analysisSummary = analysis.summary?.toLowerCase() || '';
        const solutionTitles = analysis.proposedSolutions?.map(s => s.title?.toLowerCase() || '').join(' ') || '';

        if (
          taskTitle.includes(searchLower) ||
          taskSummary.includes(searchLower) ||
          analysisSummary.includes(searchLower) ||
          solutionTitles.includes(searchLower)
        ) {
          guideResults.push({ analysis, task });
        }
      }

      setResults(guideResults);
    } catch (err: any) {
      console.error('Error searching guides:', err);
      setError(err.message || 'Failed to search guides');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Debounce search
    const timeoutId = setTimeout(() => {
      if (searchTerm.trim()) {
        searchGuides();
      } else {
        setResults([]);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  const handleViewFullGuide = (analysis: AIAnalysis) => {
    navigate(`/implementation-guides?analysisId=${analysis.id}`);
  };

  const handleCreateTask = (analysis: AIAnalysis, task: Task | null) => {
    if (task) {
      // Navigate to TaskHatch with task data pre-filled
      const params = new URLSearchParams();
      params.set('fromGuide', analysis.id);
      if (task.title) params.set('title', task.title);
      if (task.summary) params.set('summary', task.summary);
      navigate(`/task-hatch?${params.toString()}`);
    } else {
      // Create new task with analysis summary
      const params = new URLSearchParams();
      params.set('fromGuide', analysis.id);
      params.set('summary', analysis.summary || '');
      navigate(`/task-hatch?${params.toString()}`);
    }
  };

  const getTagsFromAnalysis = (analysis: AIAnalysis): string[] => {
    const tags: string[] = [];
    
    // Extract technologies from integration steps
    analysis.integrationSteps?.forEach(step => {
      step.tools?.forEach(tool => {
        if (tool && !tags.includes(tool)) {
          tags.push(tool);
        }
      });
    });

    // Extract from solutions
    analysis.proposedSolutions?.forEach(solution => {
      if (solution.approach && !tags.includes(solution.approach)) {
        tags.push(solution.approach);
      }
    });

    return tags.slice(0, 5); // Limit to 5 tags
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-indigo-950 to-purple-950 text-white p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-purple-300 via-pink-300 to-indigo-300 bg-clip-text text-transparent mb-4">
            Search Implementation Guides
          </h1>
          <p className="text-purple-200/70 text-lg">
            Find AI-generated guides to help you implement tasks and projects
          </p>
        </div>

        {/* Search Bar */}
        <div className="mb-8">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-purple-300/70" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search for implementation guides..."
              className="w-full pl-12 pr-4 py-4 bg-white/5 backdrop-blur-lg border border-purple-300/30 rounded-xl text-white placeholder-purple-300/50 focus:outline-none focus:border-purple-400 focus:ring-2 focus:ring-purple-400/50 text-lg"
            />
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-500/20 border border-red-500/50 rounded-lg text-red-300">
            {error}
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-400"></div>
            <p className="mt-4 text-purple-200">Searching guides...</p>
          </div>
        )}

        {/* Results */}
        {!loading && searchTerm.trim() && results.length === 0 && (
          <div className="text-center py-12 bg-white/5 backdrop-blur-lg rounded-xl border border-purple-300/20">
            <FileText className="w-16 h-16 text-purple-300/50 mx-auto mb-4" />
            <p className="text-purple-200 text-lg">No guides found</p>
            <p className="text-purple-300/70 mt-2">Try different search terms</p>
          </div>
        )}

        {/* Results Grid */}
        {!loading && results.length > 0 && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {results.map(({ analysis, task }) => {
              const tags = getTagsFromAnalysis(analysis);
              const solutionSummary = analysis.summary || 
                (analysis.proposedSolutions?.[0]?.description || '').substring(0, 150);
              
              return (
                <div
                  key={analysis.id}
                  className="bg-white/5 backdrop-blur-lg border border-purple-300/30 rounded-xl p-6 hover:border-purple-400/50 hover:bg-white/10 transition-all duration-300 hover:shadow-lg hover:shadow-purple-500/20"
                >
                  {/* Task Title */}
                  <h3 className="text-xl font-bold text-purple-100 mb-3 line-clamp-2">
                    {task?.title || 'Untitled Task'}
                  </h3>

                  {/* Summary */}
                  <p className="text-purple-200/80 text-sm mb-4 line-clamp-3">
                    {solutionSummary}...
                  </p>

                  {/* Tags */}
                  {tags.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-4">
                      {tags.map((tag, idx) => (
                        <span
                          key={idx}
                          className="inline-flex items-center gap-1 px-3 py-1 bg-purple-500/20 border border-purple-400/50 rounded-full text-xs text-purple-200"
                        >
                          <Tag className="w-3 h-3" />
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex gap-2 mt-6">
                    <button
                      onClick={() => handleViewFullGuide(analysis)}
                      className="flex-1 px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg font-medium hover:from-purple-600 hover:to-pink-600 transition flex items-center justify-center gap-2"
                    >
                      <ExternalLink className="w-4 h-4" />
                      View Full Guide
                    </button>
                    {currentUser && (
                      <button
                        onClick={() => handleCreateTask(analysis, task)}
                        className="px-4 py-2 bg-white/5 border border-purple-300/30 rounded-lg font-medium hover:bg-white/10 transition flex items-center justify-center gap-2"
                        title="Create Task from This"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Empty State (no search) */}
        {!loading && !searchTerm.trim() && (
          <div className="text-center py-16 bg-white/5 backdrop-blur-lg rounded-xl border border-purple-300/20">
            <Code className="w-20 h-20 text-purple-300/50 mx-auto mb-6" />
            <h2 className="text-2xl font-bold text-purple-100 mb-4">
              Start Searching
            </h2>
            <p className="text-purple-200/70 max-w-md mx-auto">
              Enter keywords to search through AI-generated implementation guides. 
              Find solutions, best practices, and step-by-step instructions.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchGuides;
